/*
 * color-mixer-paint.js — calibrated paint model + recipe solver for /color-mixer.
 *
 * No DOM. UMD: the same file runs in the page, in a Worker (importScripts) and
 * under node (tests/js/color-mixer-paint.test.mjs).
 *
 * THE MODEL is spectral.js's own Kubelka–Munk mix, reimplemented here without
 * object allocation so a recipe search can afford ~10^5 evaluations:
 *
 *   KS_mix(λ) = Σ v_i·T_i²·Y_i·KS_i(λ) / Σ v_i·T_i²·Y_i      R = 1 + KS − √(KS² + 2KS)
 *
 * v_i is the VOLUME of paint i. spectral.mix() squares its factor
 * (C = f²·T²·L), so a physical volume v is passed to it as f = √v — measured,
 * not assumed: on real tint ladders f² = volume beats f = volume for 20 of 26
 * pigments (header of scripts/color-mixer/calibrate.cjs has the numbers).
 *
 * THE LIBRARY is not a list of pretty hex codes. Each pigment is a reflectance
 * curve whose SHAPE is a real spectrophotometer reading of that paint (Golden
 * Artist Colors heavy body drawdowns, published at realtimerendering.com), with
 * a five-term smooth correction and a tinting strength fitted to measured tint
 * ladders of the same pigments with titanium white (Okumura, RIT 2005, weight
 * fractions 5–80 %). Held-out tints are predicted within ΔE2000 ≈ 1.7 on
 * average; the old hand-picked hexes were off by ≈ 14 (black at 5 % in white
 * came out near-white — a recipe built on that says "60 % black" for a mid
 * grey). The block between LIB-BEGIN and LIB-END is generated by
 * scripts/color-mixer/calibrate.cjs — regenerate, never hand-edit.
 *
 * A "paint" handed to the solver is prepared once: prepare(R, T) → { A, w }
 * where A[k] = w·KS(λk) and w = T²·Y. Everything downstream is arithmetic.
 */
(function (root, factory) {
    var P = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = P;
    if (root) root.CMXPaint = P;
}(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this), function () {
    'use strict';

    var N = 38; // 380..750 nm, 10 nm — spectral.js's grid

    /* Reflectance → XYZ, exactly spectral.js's CIE × D65 weighting (probed with
       unit spectra; tests/js/color-mixer-paint.test.mjs re-derives and compares). */
    var BX = [0.00006469199896, 0.0002194098998, 0.001120574351, 0.003766613412, 0.0118805536, 0.02328644242, 0.0345594182, 0.03722379012, 0.03241837611, 0.02123320561, 0.01049099077, 0.00329583758, 0.0005070351634, 0.0009486742057, 0.0062737181, 0.01686462419, 0.02868964903, 0.04267481247, 0.05625474813, 0.06947039727, 0.0830531517, 0.0861260963, 0.09046613768, 0.08500386506, 0.07090666911, 0.05062889164, 0.03547396189, 0.02146821026, 0.01251645676, 0.006804581639, 0.003464565795, 0.001497609751, 0.0007697004809, 0.0004073680581, 0.0001690104032, 0.00009522451504, 0.0000490309873, 0.00001999614922];
    var BY = [0.000001844289444, 0.000006205323586, 0.00003100960468, 0.0001047483849, 0.00035364053, 0.0009514714056, 0.002282263175, 0.004207329043, 0.006688798372, 0.009888396019, 0.01524945145, 0.02141831094, 0.03342293016, 0.05131001349, 0.07040208394, 0.08783870726, 0.09424905362, 0.09795667027, 0.09415218569, 0.08678102375, 0.07885653386, 0.06352670262, 0.05374141676, 0.04264606436, 0.03161734928, 0.02088520592, 0.01386011014, 0.008102640204, 0.004630102259, 0.002491380005, 0.001259303368, 0.0005416465222, 0.000277952892, 0.0001471080674, 0.00006103274729, 0.00003438732295, 0.00001770598601, 0.000007220974913];
    var BZ = [0.0003050171476, 0.001036806666, 0.005313136332, 0.01795439259, 0.05707758153, 0.1136516189, 0.1733587262, 0.1962065756, 0.1860823707, 0.1399504754, 0.08917452943, 0.04789621135, 0.0281456254, 0.0161376623, 0.007759101922, 0.004296148374, 0.002005509212, 0.0008614711099, 0.0003690387178, 0.0001914287289, 0.0001495555859, 0.00009231092851, 0.00006813491823, 0.00002882636557, 0.00001576718206, 0.000003940604103, 0.000001584012587, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

    // LIB-BEGIN
    var LIB = {
        titanium_white: { ci: 'PW6', T: 1, R: [0.9061,0.9061,0.9061,0.9061,0.9061,0.9061,0.9061,0.9061,0.9063,0.907,0.9092,0.914,0.9183,0.92,0.9206,0.9209,0.921,0.9211,0.9212,0.9213,0.9214,0.9215,0.9216,0.9217,0.9217,0.9218,0.9218,0.9218,0.9218,0.9218,0.9218,0.9218,0.9218,0.9218,0.9218,0.9218,0.9218,0.9218] },
        unbleached_titanium: { ci: 'PW6:1', T: 0.9337, R: [0.2194,0.2197,0.2208,0.3082,0.3614,0.4012,0.4412,0.4757,0.5054,0.527,0.5433,0.5542,0.5651,0.5769,0.5906,0.6027,0.6149,0.6277,0.6409,0.6544,0.6676,0.6798,0.6918,0.7029,0.7129,0.7227,0.7295,0.7376,0.7429,0.7473,0.7503,0.7528,0.7548,0.7536,0.7525,0.7515,0.7508,0.7505] },
        carbon_black: { ci: 'PBk7', T: 5.9078, R: [0.0834,0.0823,0.079,0.0735,0.068,0.0617,0.0564,0.0509,0.0473,0.0441,0.0425,0.0419,0.0414,0.043,0.0446,0.0462,0.0486,0.0502,0.0514,0.0519,0.0511,0.0497,0.0473,0.0441,0.0409,0.0378,0.0346,0.0318,0.0296,0.0278,0.0265,0.0255,0.0248,0.0244,0.0242,0.024,0.024,0.024] },
        paynes_gray: { ci: 'PB29+PBk9', T: 1.4003, R: [0.0004,0.0004,0.0007,0.0012,0.0027,0.0065,0.0156,0.0341,0.0648,0.0987,0.1213,0.12,0.0956,0.0648,0.0381,0.0207,0.0115,0.0068,0.0047,0.0038,0.0038,0.0046,0.0068,0.0115,0.0213,0.0414,0.0768,0.1315,0.2004,0.2654,0.3167,0.3415,0.3455,0.3289,0.305,0.2823,0.2666,0.261] },
        hansa_yellow: { ci: 'PY74', T: 0.8465, R: [0.1501,0.145,0.131,0.1116,0.0915,0.0725,0.059,0.048,0.0426,0.0398,0.0412,0.0493,0.0835,0.1832,0.3797,0.5962,0.7524,0.836,0.8737,0.8897,0.8942,0.8896,0.8763,0.8555,0.8307,0.8071,0.7881,0.7812,0.7832,0.7938,0.8121,0.8373,0.8655,0.8888,0.9074,0.9204,0.9279,0.9304] },
        diarylide_yellow: { ci: 'PY83', T: 1.2546, R: [0.0551,0.0552,0.0554,0.0547,0.0538,0.0529,0.052,0.052,0.053,0.0527,0.0529,0.0543,0.0568,0.0619,0.0977,0.2128,0.4063,0.5887,0.7181,0.8016,0.8442,0.8584,0.8627,0.865,0.8671,0.8711,0.8751,0.8831,0.8894,0.8948,0.8988,0.9038,0.9091,0.9128,0.916,0.9185,0.92,0.9205] },
        yellow_ochre: { ci: 'PY42', T: 0.962, R: [0.0646,0.0641,0.0627,0.0622,0.0624,0.0643,0.0697,0.0727,0.0724,0.0718,0.074,0.0791,0.0888,0.1047,0.1281,0.1605,0.2034,0.2528,0.3044,0.3513,0.3863,0.4048,0.4085,0.4025,0.3929,0.3834,0.3768,0.3752,0.377,0.3832,0.395,0.412,0.4333,0.4427,0.4513,0.4584,0.4629,0.4645] },
        raw_sienna: { ci: 'PBr7', T: 0.8708, R: [0.0566,0.0562,0.055,0.0548,0.0547,0.0565,0.0606,0.0618,0.0616,0.0605,0.0617,0.0658,0.0731,0.0843,0.0999,0.1204,0.1465,0.1766,0.2091,0.2412,0.2674,0.2842,0.2912,0.2902,0.2862,0.2819,0.2792,0.2804,0.2844,0.2919,0.3038,0.3198,0.3393,0.3495,0.3589,0.3664,0.3713,0.373] },
        burnt_sienna: { ci: 'PBr7', T: 1.2664, R: [0.0601,0.0594,0.0573,0.0544,0.0511,0.0478,0.045,0.0419,0.04,0.0382,0.0377,0.0383,0.0394,0.0415,0.0442,0.0476,0.0521,0.0582,0.067,0.0789,0.0933,0.1083,0.122,0.1335,0.1437,0.1529,0.1625,0.1749,0.1903,0.2086,0.2316,0.2577,0.2872,0.3064,0.324,0.3381,0.3473,0.3505] },
        red_oxide: { ci: 'PR101', T: 2.023, R: [0.073,0.0728,0.072,0.0709,0.0699,0.0679,0.0659,0.0635,0.0617,0.0597,0.0582,0.0575,0.0562,0.0562,0.0566,0.0572,0.0595,0.0643,0.0765,0.1019,0.1419,0.1883,0.2274,0.25,0.2577,0.2557,0.2483,0.2398,0.2313,0.2227,0.2155,0.21,0.2065,0.1919,0.1803,0.172,0.167,0.1653] },
        raw_umber: { ci: 'PBr7', T: 2.1244, R: [0.0129,0.0133,0.0148,0.0175,0.0214,0.0273,0.035,0.043,0.0516,0.0574,0.0618,0.0634,0.061,0.0575,0.0522,0.047,0.0429,0.0399,0.0384,0.0383,0.04,0.043,0.0475,0.0526,0.0586,0.0636,0.0672,0.0682,0.0664,0.0607,0.0533,0.0454,0.0381,0.0317,0.0267,0.0234,0.0214,0.0208] },
        burnt_umber: { ci: 'PBr7', T: 1.7077, R: [0.0385,0.0383,0.0378,0.0371,0.0364,0.0355,0.0347,0.0341,0.0337,0.0336,0.0337,0.0348,0.0356,0.0376,0.0397,0.0421,0.0454,0.0491,0.0533,0.0578,0.062,0.0658,0.0695,0.0717,0.0735,0.0748,0.0748,0.0752,0.0753,0.0749,0.0746,0.0748,0.0749,0.0741,0.0735,0.0731,0.0729,0.0728] },
        pyrrole_red: { ci: 'PR254', T: 1.7429, R: [0.0231,0.0238,0.0258,0.0287,0.0333,0.0399,0.0475,0.0545,0.0617,0.0648,0.0651,0.0631,0.0574,0.0518,0.0449,0.0384,0.0345,0.0326,0.0329,0.0334,0.0381,0.1053,0.2781,0.4783,0.6448,0.763,0.8309,0.8647,0.8793,0.8847,0.8838,0.8779,0.8681,0.8443,0.8197,0.7979,0.7828,0.7774] },
        naphthol_red: { ci: 'PR170', T: 1.8918, R: [0.0551,0.0554,0.0563,0.0571,0.0583,0.0586,0.0592,0.0569,0.0551,0.0508,0.0457,0.0407,0.0353,0.0317,0.0288,0.0266,0.0258,0.0265,0.0299,0.038,0.0568,0.0978,0.1726,0.2821,0.414,0.5418,0.6463,0.7207,0.7561,0.7574,0.7318,0.6841,0.6239,0.5366,0.4578,0.3975,0.3606,0.3483] },
        quinacridone_crimson: { ci: 'PR206', T: 2.0293, R: [0.0962,0.0956,0.0938,0.0882,0.0829,0.0763,0.0704,0.0622,0.0556,0.0479,0.0419,0.0368,0.0325,0.0302,0.029,0.0292,0.0311,0.0348,0.0416,0.0512,0.0644,0.0837,0.1173,0.1669,0.2301,0.2822,0.3069,0.3027,0.2744,0.2267,0.1686,0.1229,0.0967,0.065,0.0455,0.0345,0.0288,0.0271] },
        quinacridone_magenta: { ci: 'PR122', T: 1.3756, R: [0.0645,0.0667,0.0733,0.0826,0.091,0.0997,0.1046,0.1036,0.1,0.0898,0.0757,0.0607,0.0462,0.0351,0.0268,0.0218,0.0196,0.0191,0.0208,0.0261,0.0368,0.0584,0.101,0.1832,0.3405,0.5297,0.6642,0.7261,0.7278,0.6789,0.583,0.4526,0.32,0.1969,0.1207,0.08,0.061,0.0556] },
        dioxazine_purple: { ci: 'PV23', T: 2.3856, R: [0.0009,0.001,0.0016,0.0031,0.0073,0.0182,0.0442,0.0916,0.1545,0.2035,0.2104,0.1752,0.1133,0.0617,0.0298,0.0141,0.0072,0.0043,0.0032,0.0032,0.0041,0.0067,0.0126,0.0254,0.0523,0.1017,0.1693,0.2298,0.2595,0.242,0.1905,0.1278,0.0786,0.0354,0.0166,0.0089,0.0059,0.0052] },
        ultramarine_blue: { ci: 'PB29', T: 1.5493, R: [0.2396,0.2407,0.2438,0.2854,0.3402,0.4027,0.4595,0.477,0.4301,0.3275,0.2148,0.1513,0.1222,0.1066,0.0919,0.0781,0.067,0.0571,0.0492,0.0434,0.0389,0.0358,0.0344,0.0337,0.0342,0.0358,0.0384,0.0425,0.0479,0.0544,0.0622,0.0721,0.0843,0.0927,0.1003,0.1063,0.1101,0.1115] },
        cobalt_blue: { ci: 'PB28', T: 0.9241, R: [0.4221,0.4175,0.4041,0.4112,0.4428,0.4458,0.4186,0.3726,0.3355,0.2839,0.2438,0.262,0.2386,0.1622,0.0974,0.0666,0.0553,0.051,0.0468,0.0412,0.0358,0.0315,0.0284,0.026,0.0244,0.0248,0.0283,0.0402,0.0763,0.171,0.3727,0.6521,0.8543,0.8956,0.9228,0.9392,0.9479,0.9506] },
        cerulean_blue: { ci: 'PB36', T: 1.0238, R: [0.3348,0.3324,0.3255,0.3121,0.3107,0.3064,0.3077,0.3252,0.3478,0.3532,0.337,0.3432,0.3346,0.2753,0.1966,0.1365,0.0985,0.0788,0.0715,0.0648,0.058,0.0537,0.0519,0.0507,0.0484,0.0466,0.0487,0.062,0.1029,0.1935,0.3463,0.5237,0.6754,0.6997,0.7203,0.7359,0.7456,0.7489] },
        phthalo_blue: { ci: 'PB15:4', T: 1.8801, R: [0.1523,0.1513,0.1485,0.1568,0.1514,0.1502,0.1732,0.1902,0.1789,0.1542,0.1247,0.1001,0.0804,0.0682,0.0584,0.0491,0.04,0.0305,0.0221,0.0157,0.0111,0.008,0.006,0.0048,0.0043,0.0043,0.0048,0.0061,0.0088,0.0136,0.0232,0.0413,0.0758,0.1273,0.1939,0.2615,0.3114,0.3296] },
        anthraquinone_blue: { ci: 'PB60', T: 2.4681, R: [0.3036,0.2985,0.2839,0.2766,0.2477,0.2041,0.1629,0.1276,0.1025,0.0857,0.0739,0.0668,0.0605,0.0571,0.0539,0.0505,0.0473,0.0434,0.0392,0.035,0.0305,0.0262,0.0228,0.0201,0.0182,0.017,0.0168,0.0174,0.0191,0.0219,0.0265,0.0331,0.0419,0.0511,0.0609,0.0697,0.076,0.0783] },
        phthalo_green: { ci: 'PG7', T: 1.5483, R: [0.4246,0.4042,0.3477,0.2699,0.1916,0.1293,0.0874,0.0626,0.0533,0.0536,0.0659,0.0871,0.1089,0.1276,0.1408,0.1505,0.1536,0.1419,0.1149,0.0789,0.0456,0.0232,0.011,0.0053,0.0028,0.0017,0.0012,0.0012,0.0016,0.0026,0.0054,0.013,0.0334,0.0831,0.1776,0.3006,0.4007,0.4377] },
        permanent_green_light: { ci: 'PG7+PY3', T: 1.0767, R: [0.1805,0.1732,0.1531,0.1262,0.0991,0.0748,0.0572,0.0439,0.0368,0.0394,0.0811,0.1581,0.237,0.2776,0.2833,0.2734,0.2493,0.211,0.1661,0.1244,0.0884,0.0604,0.0415,0.0293,0.0219,0.0171,0.0143,0.0131,0.0135,0.0153,0.0191,0.0252,0.0343,0.0461,0.0603,0.0745,0.0854,0.0895] },
        chromium_oxide_green: { ci: 'PG17', T: 1.5278, R: [0.0852,0.0869,0.092,0.1284,0.1153,0.094,0.0847,0.085,0.0906,0.0981,0.1077,0.1218,0.1351,0.1566,0.2033,0.2424,0.2328,0.1845,0.135,0.1029,0.0869,0.0821,0.0862,0.0969,0.1143,0.1374,0.1674,0.2067,0.2542,0.3026,0.3477,0.3876,0.4246,0.4311,0.4345,0.4361,0.4366,0.4367] },
        deep_green: { ci: 'PG7+PBr7', T: 1.9237, R: [0.6921,0.658,0.5507,0.3776,0.2034,0.0888,0.0364,0.0158,0.0085,0.0059,0.0057,0.0066,0.0089,0.0141,0.0246,0.0426,0.0676,0.0914,0.1011,0.0898,0.063,0.0361,0.0176,0.0079,0.0037,0.0019,0.0012,0.001,0.0011,0.0015,0.0029,0.0065,0.0164,0.0415,0.0937,0.1721,0.2461,0.2761] },
        green_gold: { ci: 'PY150+PG36', T: 0.7305, R: [0.0008,0.0009,0.0011,0.0014,0.0021,0.0033,0.0054,0.0087,0.0146,0.0242,0.0455,0.0794,0.1166,0.1458,0.167,0.1828,0.1949,0.2043,0.2104,0.2124,0.2151,0.2155,0.2045,0.1879,0.182,0.1862,0.1909,0.1885,0.1824,0.1756,0.1683,0.16,0.1511,0.1289,0.1111,0.0984,0.0909,0.0884] },
    };
    var LIB_ORDER = ['titanium_white', 'unbleached_titanium', 'carbon_black', 'paynes_gray', 'hansa_yellow', 'diarylide_yellow', 'yellow_ochre', 'raw_sienna', 'burnt_sienna', 'red_oxide', 'raw_umber', 'burnt_umber', 'pyrrole_red', 'naphthol_red', 'quinacridone_crimson', 'quinacridone_magenta', 'dioxazine_purple', 'ultramarine_blue', 'cobalt_blue', 'cerulean_blue', 'phthalo_blue', 'anthraquinone_blue', 'phthalo_green', 'permanent_green_light', 'chromium_oxide_green', 'deep_green', 'green_gold'];
    // LIB-END

    /* ---- elementary optics --------------------------------------------------- */

    function ksOf(r) {
        if (r < 1e-6) r = 1e-6; else if (r > 1) r = 1;
        return (1 - r) * (1 - r) / (2 * r);
    }
    function rOfKs(x) { return 1 + x - Math.sqrt(x * x + 2 * x); }

    function xyzOfR(R) {
        var X = 0, Y = 0, Z = 0;
        for (var k = 0; k < N; k++) { X += R[k] * BX[k]; Y += R[k] * BY[k]; Z += R[k] * BZ[k]; }
        return [X, Y, Z];
    }

    var WX = 0.95047, WY = 1.0, WZ = 1.08883;
    function labF(t) { return t > 216 / 24389 ? Math.cbrt(t) : (24389 / 27 * t + 16) / 116; }
    function labOfXyz(xyz) {
        var fx = labF(xyz[0] / WX), fy = labF(xyz[1] / WY), fz = labF(xyz[2] / WZ);
        return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
    }
    function xyzOfLab(lab) {
        var fy = (lab[0] + 16) / 116, fx = fy + lab[1] / 500, fz = fy - lab[2] / 200;
        var inv = function (t) { var t3 = t * t * t; return t3 > 216 / 24389 ? t3 : (116 * t - 16) / (24389 / 27); };
        return [inv(fx) * WX, inv(fy) * WY, inv(fz) * WZ];
    }

    function s2l(c) { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
    function l2s(c) {
        var v = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
        return Math.max(0, Math.min(255, Math.round(v * 255)));
    }
    function labOfRgb(rgb) {
        var r = s2l(rgb[0]), g = s2l(rgb[1]), b = s2l(rgb[2]);
        return labOfXyz([
            0.4124564 * r + 0.3575761 * g + 0.1804375 * b,
            0.2126729 * r + 0.7151522 * g + 0.0721750 * b,
            0.0193339 * r + 0.1191920 * g + 0.9503041 * b
        ]);
    }
    /* Display only: XYZ → sRGB with chroma reduced toward the same lightness
       until it fits (a per-channel clip would skew hue — cadmium-ish yellows
       and phthalo tints sit outside sRGB and must still read as themselves). */
    function linOfXyz(x) {
        return [
            3.2404542 * x[0] - 1.5371385 * x[1] - 0.4985314 * x[2],
            -0.9692660 * x[0] + 1.8760108 * x[1] + 0.0415560 * x[2],
            0.0556434 * x[0] - 0.2040259 * x[1] + 1.0572252 * x[2]
        ];
    }
    function fits(lin) { return lin[0] >= -1e-4 && lin[1] >= -1e-4 && lin[2] >= -1e-4 && lin[0] <= 1.0001 && lin[1] <= 1.0001 && lin[2] <= 1.0001; }
    function rgbOfXyz(xyz) {
        var lin = linOfXyz(xyz);
        if (!fits(lin)) {
            var lab = labOfXyz(xyz), lo = 0, hi = 1;
            for (var i = 0; i < 22; i++) {
                var mid = (lo + hi) / 2;
                var t = linOfXyz(xyzOfLab([lab[0], lab[1] * mid, lab[2] * mid]));
                if (fits(t)) lo = mid; else hi = mid;
            }
            lin = linOfXyz(xyzOfLab([Math.max(0, Math.min(100, lab[0])), lab[1] * lo, lab[2] * lo]));
        }
        return [l2s(lin[0]), l2s(lin[1]), l2s(lin[2])];
    }
    function rgbOfLab(lab) { return rgbOfXyz(xyzOfLab(lab)); }
    function hexOfRgb(rgb) {
        return '#' + rgb.map(function (v) { return Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0'); }).join('');
    }

    /* ---- CIEDE2000 (Sharma, Wu & Dalal 2005; same code as color-pick-core.js,
       which is pinned against their test vectors) ---------------------------- */
    var RAD = Math.PI / 180, DEG = 180 / Math.PI, P25_7 = Math.pow(25, 7);
    function deltaE2000(l1, l2) {
        var L1 = l1[0], a1 = l1[1], b1 = l1[2], L2 = l2[0], a2 = l2[1], b2 = l2[2];
        var C1 = Math.sqrt(a1 * a1 + b1 * b1), C2 = Math.sqrt(a2 * a2 + b2 * b2);
        var Cb = (C1 + C2) / 2, Cb7 = Math.pow(Cb, 7);
        var G = 0.5 * (1 - Math.sqrt(Cb7 / (Cb7 + P25_7)));
        var a1p = a1 * (1 + G), a2p = a2 * (1 + G);
        var C1p = Math.sqrt(a1p * a1p + b1 * b1), C2p = Math.sqrt(a2p * a2p + b2 * b2);
        var h1p = C1p === 0 ? 0 : Math.atan2(b1, a1p) * DEG; if (h1p < 0) h1p += 360;
        var h2p = C2p === 0 ? 0 : Math.atan2(b2, a2p) * DEG; if (h2p < 0) h2p += 360;
        var dLp = L2 - L1, dCp = C2p - C1p, dhp;
        if (C1p * C2p === 0) dhp = 0;
        else if (Math.abs(h2p - h1p) <= 180) dhp = h2p - h1p;
        else dhp = h2p - h1p > 180 ? h2p - h1p - 360 : h2p - h1p + 360;
        var dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin(dhp / 2 * RAD);
        var Lb = (L1 + L2) / 2, Cpb = (C1p + C2p) / 2, hpb;
        if (C1p * C2p === 0) hpb = h1p + h2p;
        else if (Math.abs(h1p - h2p) <= 180) hpb = (h1p + h2p) / 2;
        else hpb = h1p + h2p < 360 ? (h1p + h2p + 360) / 2 : (h1p + h2p - 360) / 2;
        var T = 1 - 0.17 * Math.cos((hpb - 30) * RAD) + 0.24 * Math.cos(2 * hpb * RAD)
            + 0.32 * Math.cos((3 * hpb + 6) * RAD) - 0.20 * Math.cos((4 * hpb - 63) * RAD);
        var dTh = 30 * Math.exp(-Math.pow((hpb - 275) / 25, 2));
        var Cpb7 = Math.pow(Cpb, 7);
        var RC = 2 * Math.sqrt(Cpb7 / (Cpb7 + P25_7));
        var L50 = (Lb - 50) * (Lb - 50);
        var SL = 1 + 0.015 * L50 / Math.sqrt(20 + L50), SC = 1 + 0.045 * Cpb, SH = 1 + 0.015 * Cpb * T;
        var RT = -Math.sin(2 * dTh * RAD) * RC;
        var x = dLp / SL, y = dCp / SC, z = dHp / SH;
        return Math.sqrt(x * x + y * y + z * z + RT * y * z);
    }

    /* ---- paints ---------------------------------------------------------------
     * prepare(R, T, meta) → { R, T, Y, w, A, ...meta }. R is a 38-point
     * reflectance, T the tinting strength. A custom colour arrives as the
     * reflectance spectral.js reconstructs from its sRGB (the page does that —
     * this file does not ship spectral.js's basis), with T = 1 unless the user
     * said the paint is weak or strong. */
    function prepare(R, T, meta) {
        var p = meta ? Object.assign({}, meta) : {};
        var r = new Float64Array(N);
        for (var k = 0; k < N; k++) { var v = +R[k]; r[k] = v < 1e-6 ? 1e-6 : v > 0.9999 ? 0.9999 : v; }
        var Y = Math.max(1e-12, xyzOfR(r)[1]);
        T = T > 0 ? T : 1;
        var w = T * T * Y, A = new Float64Array(N);
        for (var j = 0; j < N; j++) A[j] = w * ksOf(r[j]);
        p.R = r; p.T = T; p.Y = Y; p.w = w; p.A = A;
        p.lab = labOfXyz(xyzOfR(r));
        return p;
    }
    /* The same paint, s times stronger (s multiplies T², i.e. its weight in
       every mix). Used for strengths learned from a batch the person mixed. */
    function adjust(p, s) {
        if (!(s > 0) || s === 1) return p;
        var q = Object.assign({}, p), A = new Float64Array(N);
        for (var k = 0; k < N; k++) A[k] = p.A[k] * s;
        q.A = A; q.w = p.w * s; q.T = p.T * Math.sqrt(s);
        return q;
    }

    /* A paint that is itself a fixed mix of library pigments — a brand colour
       modelled from its published pigments (public/js/color-mixer-brands.js).
       Exact within the model: in any mix, v units of it act as its components
       at v·u_j/U. Returned in prepare()'s shape, so the worker receives it as
       R + T like any other paint; s scales its weight (student paints carry
       less pigment). */
    function composite(parts, s, meta) {
        var U = 0, w = 0, KS = new Float64Array(N), i, k;
        for (i = 0; i < parts.length; i++) {
            var p = pigment(parts[i][0]), u = +parts[i][1];
            if (!p || !(u > 0)) continue;
            U += u; w += u * p.w;
            for (k = 0; k < N; k++) KS[k] += u * p.A[k];
        }
        if (!(w > 0)) return null;
        var R = new Float64Array(N);
        for (k = 0; k < N; k++) R[k] = rOfKs(KS[k] / w);
        var Y = Math.max(1e-12, xyzOfR(R)[1]);
        return prepare(R, Math.sqrt((w / U) * (s > 0 ? s : 1) / Y), meta);
    }

    var _pigCache = {};
    function pigment(id) {
        if (_pigCache[id]) return _pigCache[id];
        var d = LIB[id]; if (!d) return null;
        _pigCache[id] = prepare(d.R, d.T, { id: id, ci: d.ci, calibrated: true });
        return _pigCache[id];
    }
    function libraryIds() { return LIB_ORDER.slice(); }

    /* Mix reflectance for paints[] at volumes vols[] (any positive scale). */
    function mixR(paints, vols, out) {
        out = out || new Float64Array(N);
        var den = 0, m = paints.length, i, k;
        for (i = 0; i < m; i++) den += (vols[i] > 0 ? vols[i] : 0) * paints[i].w;
        if (!(den > 0)) { for (k = 0; k < N; k++) out[k] = 0; return out; }
        for (k = 0; k < N; k++) {
            var s = 0;
            for (i = 0; i < m; i++) if (vols[i] > 0) s += vols[i] * paints[i].A[k];
            out[k] = rOfKs(s / den);
        }
        return out;
    }
    var _scratch = new Float64Array(N);
    function mixXyz(paints, vols) { return xyzOfR(mixR(paints, vols, _scratch)); }
    function mixLab(paints, vols) { return labOfXyz(mixXyz(paints, vols)); }
    function mixRgb(paints, vols) { return rgbOfXyz(mixXyz(paints, vols)); }
    function paintRgb(p) { return rgbOfXyz(xyzOfR(p.R)); }
    /* A tint is how a dark paint is recognised: its masstone is near-black. */
    function tintRgb(p, white, frac) {
        return mixRgb([p, white], [frac, 1 - frac]);
    }

    /* ---- the evaluator: hot loop of every search ------------------------------ */
    function makeEval(paints, target) {
        var lab = [0, 0, 0];
        return function (idx, vols) {
            var den = 0, m = idx.length, i;
            for (i = 0; i < m; i++) den += vols[i] * paints[idx[i]].w;
            if (!(den > 0)) return 1e9;
            var inv = 1 / den, X = 0, Y = 0, Z = 0;
            var A0 = paints[idx[0]].A, v0 = vols[0] * inv;
            var A1 = m > 1 ? paints[idx[1]].A : null, v1 = m > 1 ? vols[1] * inv : 0;
            var A2 = m > 2 ? paints[idx[2]].A : null, v2 = m > 2 ? vols[2] * inv : 0;
            var A3 = m > 3 ? paints[idx[3]].A : null, v3 = m > 3 ? vols[3] * inv : 0;
            for (var k = 0; k < N; k++) {
                var s = v0 * A0[k];
                if (A1) s += v1 * A1[k];
                if (A2) s += v2 * A2[k];
                if (A3) s += v3 * A3[k];
                var r = 1 + s - Math.sqrt(s * s + 2 * s);
                X += r * BX[k]; Y += r * BY[k]; Z += r * BZ[k];
            }
            var fx = labF(X / WX), fy = labF(Y / WY), fz = labF(Z / WZ);
            lab[0] = 116 * fy - 16; lab[1] = 500 * (fx - fy); lab[2] = 200 * (fy - fz);
            return deltaE2000(target, lab);
        };
    }

    /* Volumes from unconstrained coordinates: v = softmax([0, θ…]). */
    function volsOf(theta, out) {
        var m = theta.length + 1, mx = 0, i;
        for (i = 0; i < theta.length; i++) if (theta[i] > mx) mx = theta[i];
        var s = Math.exp(-mx); out[0] = s;
        for (i = 0; i < theta.length; i++) { out[i + 1] = Math.exp(theta[i] - mx); s += out[i + 1]; }
        for (i = 0; i < m; i++) out[i] /= s;
        return out;
    }
    function thetaOf(vols) {
        var t = [], b = Math.log(Math.max(1e-9, vols[0]));
        for (var i = 1; i < vols.length; i++) t.push(Math.log(Math.max(1e-9, vols[i])) - b);
        return t;
    }

    /* Nelder–Mead, small and allocation-light. */
    function nelderMead(f, x0, step, iters) {
        var n = x0.length, S = [x0.slice()], F, i, j;
        for (i = 0; i < n; i++) { var x = x0.slice(); x[i] += step; S.push(x); }
        F = S.map(f);
        var c = new Array(n), xr = new Array(n), xe = new Array(n), xc = new Array(n);
        for (var it = 0; it < iters; it++) {
            // order
            for (i = 1; i <= n; i++) {
                for (j = i; j > 0 && F[j] < F[j - 1]; j--) {
                    var tf = F[j]; F[j] = F[j - 1]; F[j - 1] = tf;
                    var ts = S[j]; S[j] = S[j - 1]; S[j - 1] = ts;
                }
            }
            if (F[n] - F[0] < 1e-5 && it > 8) break;
            for (j = 0; j < n; j++) { c[j] = 0; for (i = 0; i < n; i++) c[j] += S[i][j]; c[j] /= n; }
            for (j = 0; j < n; j++) xr[j] = c[j] + (c[j] - S[n][j]);
            var fr = f(xr);
            if (fr < F[0]) {
                for (j = 0; j < n; j++) xe[j] = c[j] + 2 * (c[j] - S[n][j]);
                var fe = f(xe);
                if (fe < fr) { S[n] = xe.slice(); F[n] = fe; } else { S[n] = xr.slice(); F[n] = fr; }
            } else if (fr < F[n - 1]) { S[n] = xr.slice(); F[n] = fr; }
            else {
                for (j = 0; j < n; j++) xc[j] = c[j] + 0.5 * (S[n][j] - c[j]);
                var fc = f(xc);
                if (fc < F[n]) { S[n] = xc.slice(); F[n] = fc; }
                else {
                    for (i = 1; i <= n; i++) { for (j = 0; j < n; j++) S[i][j] = S[0][j] + 0.5 * (S[i][j] - S[0][j]); F[i] = f(S[i]); }
                }
            }
        }
        var b = 0; for (i = 1; i <= n; i++) if (F[i] < F[b]) b = i;
        return { x: S[b], f: F[b] };
    }

    /* ---- recipe search ---------------------------------------------------------
     * target: Lab. paints: prepared paints. opts.maxPaints 1..4 (default 3).
     * Returns { byCount: [null, rec1, rec2, …], ranked: [...] } where a recipe is
     * { idx: [..], vols: [..] (sum 1), dE }. A component under MIN_FRAC does not
     * count — that recipe is really one paint shorter and is dropped from its
     * slot, so "3 paints" never means "2 paints plus a speck". */
    var MIN_FRAC = 0.003;

    function solve(target, paints, opts) {
        opts = opts || {};
        var kmax = Math.max(1, Math.min(4, opts.maxPaints || 3));
        var p = paints.length;
        var ev = makeEval(paints, target);
        var byCount = [null, null, null, null, null];
        var pool = [];
        var evals = 0;
        var fEval = function (idx, vols) { evals++; return ev(idx, vols); };
        function consider(rec) {
            for (var i = 0; i < rec.vols.length; i++) if (rec.vols[i] < MIN_FRAC) return;
            var k = rec.idx.length;
            if (!byCount[k] || rec.dE < byCount[k].dE) byCount[k] = rec;
            pool.push(rec);
        }
        if (!p) return { byCount: byCount, ranked: [], evals: 0 };

        // 1 paint
        for (var i = 0; i < p; i++) consider({ idx: [i], vols: [1], dE: fEval([i], [1]) });

        // 2 paints: logit scan + golden section on s = share of the second
        var pairs = [];
        var tmp2 = [0, 0];
        if (kmax >= 2 && p >= 2) {
            for (var a = 0; a < p; a++) for (var b = a + 1; b < p; b++) {
                var idx2 = [a, b];
                var g = function (t) { var s = 1 / (1 + Math.exp(-t)); tmp2[0] = 1 - s; tmp2[1] = s; return fEval(idx2, tmp2); };
                var bt = 0, bf = Infinity;
                for (var t0 = -7.5; t0 <= 7.51; t0 += 0.5) { var f0 = g(t0); if (f0 < bf) { bf = f0; bt = t0; } }
                var lo = bt - 0.5, hi = bt + 0.5, gr = 0.6180339887;
                var x1 = hi - gr * (hi - lo), x2 = lo + gr * (hi - lo), f1 = g(x1), f2 = g(x2);
                for (var q = 0; q < 16; q++) {
                    if (f1 < f2) { hi = x2; x2 = x1; f2 = f1; x1 = hi - gr * (hi - lo); f1 = g(x1); }
                    else { lo = x1; x1 = x2; f1 = f2; x2 = lo + gr * (hi - lo); f2 = g(x2); }
                }
                var tb = f1 < f2 ? x1 : x2, fb = Math.min(f1, f2);
                if (bf < fb) { tb = bt; fb = bf; }
                var sb = 1 / (1 + Math.exp(-tb));
                var rec2 = { idx: [a, b], vols: [1 - sb, sb], dE: fb };
                pairs.push(rec2);
                consider(rec2);
            }
            pairs.sort(function (x, y) { return x.dE - y.dE; });
        }

        // 3 paints: every triple, Nelder–Mead from its best pair (+ a pinch of
        // the third) — exhaustive below 30 paints, pruned above.
        var triples = [];
        if (kmax >= 3 && p >= 3) {
            var pairBest = {};
            for (var pi = 0; pi < pairs.length; pi++) pairBest[pairs[pi].idx[0] + ',' + pairs[pi].idx[1]] = pairs[pi];
            var tripleSet = [];
            if (p <= 30) {
                for (var i3 = 0; i3 < p; i3++) for (var j3 = i3 + 1; j3 < p; j3++) for (var k3 = j3 + 1; k3 < p; k3++) tripleSet.push([i3, j3, k3]);
            } else {
                var seen = {};
                var topPairs = pairs.slice(0, 60);
                topPairs.forEach(function (pr) {
                    for (var z = 0; z < p; z++) {
                        if (z === pr.idx[0] || z === pr.idx[1]) continue;
                        var tr = [pr.idx[0], pr.idx[1], z].sort(function (x, y) { return x - y; });
                        var key = tr.join(',');
                        if (!seen[key]) { seen[key] = 1; tripleSet.push(tr); }
                    }
                });
            }
            var vol3 = [0, 0, 0];
            for (var ti = 0; ti < tripleSet.length; ti++) {
                var tri = tripleSet[ti];
                var seeds = [[tri[0], tri[1], tri[2]], [tri[0], tri[2], tri[1]], [tri[1], tri[2], tri[0]]];
                var bestSeed = null;
                for (var sd = 0; sd < 3; sd++) {
                    var s0 = seeds[sd], pr0 = pairBest[Math.min(s0[0], s0[1]) + ',' + Math.max(s0[0], s0[1])];
                    if (pr0 && (!bestSeed || pr0.dE < bestSeed.pr.dE)) bestSeed = { pr: pr0, third: s0[2] };
                }
                var v0 = [0, 0, 0];
                var pos = {}; pos[tri[0]] = 0; pos[tri[1]] = 1; pos[tri[2]] = 2;
                v0[pos[bestSeed.pr.idx[0]]] = bestSeed.pr.vols[0] * 0.97;
                v0[pos[bestSeed.pr.idx[1]]] = bestSeed.pr.vols[1] * 0.97;
                v0[pos[bestSeed.third]] = 0.03;
                var h3 = function (th) { volsOf(th, vol3); return fEval(tri, vol3); };
                var r3 = nelderMead(h3, thetaOf(v0), 0.7, 60);
                var rec3 = { idx: tri.slice(), vols: volsOf(r3.x, [0, 0, 0]), dE: r3.f };
                triples.push(rec3);
                consider(rec3);
            }
            triples.sort(function (x, y) { return x.dE - y.dE; });
            // polish the leaders from other starts
            for (var tp = 0; tp < Math.min(16, triples.length); tp++) {
                var T3 = triples[tp];
                var h3b = function (th) { volsOf(th, vol3); return fEval(T3.idx, vol3); };
                [[0.34, 0.33, 0.33], [0.8, 0.1, 0.1], [0.1, 0.8, 0.1], [0.1, 0.1, 0.8]].forEach(function (st) {
                    var r = nelderMead(h3b, thetaOf(st), 0.9, 80);
                    if (r.f < T3.dE) { T3.dE = r.f; T3.vols = volsOf(r.x, [0, 0, 0]); }
                });
                consider(T3);
            }
            triples.sort(function (x, y) { return x.dE - y.dE; });
        }

        // 4 paints: extend the best triples by one paint.
        if (kmax >= 4 && p >= 4) {
            var seen4 = {}, vol4 = [0, 0, 0, 0];
            var lead = triples.slice(0, 36);
            for (var li = 0; li < lead.length; li++) {
                var T0 = lead[li];
                for (var z4 = 0; z4 < p; z4++) {
                    if (T0.idx.indexOf(z4) >= 0) continue;
                    var quad = T0.idx.concat([z4]);
                    var key4 = quad.slice().sort(function (x, y) { return x - y; }).join(',');
                    if (seen4[key4]) continue; seen4[key4] = 1;
                    var vq = [T0.vols[0] * 0.97, T0.vols[1] * 0.97, T0.vols[2] * 0.97, 0.03];
                    var h4 = function (th) { volsOf(th, vol4); return fEval(quad, vol4); };
                    var r4 = nelderMead(h4, thetaOf(vq), 0.7, 90);
                    consider({ idx: quad, vols: volsOf(r4.x, [0, 0, 0, 0]), dE: r4.f });
                }
            }
        }

        // ranked, one entry per distinct paint set
        pool.sort(function (x, y) { return x.dE - y.dE; });
        var ranked = [], keys = {};
        for (var r = 0; r < pool.length && ranked.length < 24; r++) {
            var key = pool[r].idx.slice().sort(function (x, y) { return x - y; }).join(',');
            if (keys[key]) continue; keys[key] = 1; ranked.push(pool[r]);
        }
        return { byCount: byCount, ranked: ranked, evals: evals };
    }

    /* The recipe we recommend: the fewest paints that are not visibly worse
       than the best. One more tube that buys ΔE 0.4 is not worth the mess —
       and below ΔE ≈ 2 the difference is smaller than the model's own
       error on real paint (held-out 1.7), so it is not a difference at all:
       "white + black" for a mid grey (ΔE 1.8) beats a three-pigment
       chromatic grey that is 1.7 units "closer" on paper. */
    function recommend(byCount) {
        var best = null, k;
        for (k = 1; k < byCount.length; k++) if (byCount[k] && (!best || byCount[k].dE < best.dE)) best = byCount[k];
        if (!best) return null;
        var bar = Math.max(2.0, best.dE + Math.max(0.8, best.dE * 0.2));
        for (k = 1; k < byCount.length; k++) if (byCount[k] && byCount[k].dE <= bar) return byCount[k];
        return best;
    }
    /* Slots worth showing: each extra paint must actually help. */
    function usefulCounts(byCount) {
        var out = [], prev = Infinity;
        for (var k = 1; k < byCount.length; k++) {
            var r = byCount[k];
            if (r && r.dE < prev - 0.05) { out.push(k); prev = r.dE; }
        }
        return out;
    }

    /* ---- whole-number parts ------------------------------------------------------
     * Fractions become parts a person can measure with a spoon. The smallest
     * total whose result stays within `tol` of the exact recipe wins; the ΔE
     * shown is the ΔE OF THE PARTS, not of the fractions behind them. */
    function toParts(paints, rec, target, opts) {
        opts = opts || {};
        var ev = makeEval(paints, target);
        var tol = opts.tol != null ? opts.tol : Math.max(0.35, rec.dE * 0.1);
        var maxTotal = opts.maxTotal || 60;
        var m = rec.idx.length, best = null;
        if (m === 1) return { parts: [1], dE: rec.dE, total: 1 };
        for (var S = m; S <= maxTotal; S++) {
            var parts = rec.vols.map(function (v) { return Math.max(1, Math.round(v * S)); });
            var tot = parts.reduce(function (s, x) { return s + x; }, 0);
            var dE = ev(rec.idx, parts);
            if (!best || dE < best.dE - 1e-9) best = { parts: parts, dE: dE, total: tot };
            if (dE <= rec.dE + tol) return { parts: parts, dE: dE, total: tot };
        }
        return best;
    }

    /* ---- fix a batch that came out wrong ----------------------------------------
     * The person mixed `rec` and it came out as `observed` (Lab). Real paints
     * differ from the model mostly in STRENGTH, so first explain the miss by
     * per-paint strength factors (regularised toward 1, clamped to ¼…4) plus
     * whatever offset remains; then search what to ADD — paint cannot be taken
     * back out — to land on the target. Returns the options best-first. */
    function correct(target, paints, rec, observed, opts) {
        opts = opts || {};
        // Work in fractions of the batch the person already has; additions come
        // back in the caller's units (parts in, parts out). Up to 1.5× the batch
        // again may be added — beyond that "start over" is the honest answer.
        var maxAdd = opts.maxAdd || 1.5;
        // Adding a whole second batch must buy at least ~0.5 ΔE to be worth it.
        var ADD_COST = opts.addCost != null ? opts.addCost : 0.5;
        var m = rec.idx.length;
        var total = 0; rec.vols.forEach(function (v) { total += v; });
        if (!(total > 0)) total = 1;
        var base = rec.vols.map(function (v) { return v / total; });
        // 1) strength refit on the paints in the recipe. Strength scales w and
        //    A together (A = w·KS), i.e. it is T² — only RATIOS are observable,
        //    so the first paint of the recipe stays the reference.
        var sub = rec.idx.map(function (i) { return paints[i]; });
        var clampT = function (t) { return Math.exp(Math.max(-1.386, Math.min(1.386, t))); };
        var fitF = function (th) {
            var ps = sub.map(function (pt, j) { return j === 0 ? pt : adjust(pt, clampT(th[j - 1])); });
            var lab = labOfXyz(xyzOfR(mixR(ps, base, new Float64Array(N))));
            var reg = 0; for (var j = 0; j < th.length; j++) reg += th[j] * th[j];
            return deltaE2000(observed, lab) + 0.6 * reg;
        };
        var th0 = new Array(Math.max(0, m - 1)).fill(0);
        var fitRes = m > 1 ? nelderMead(fitF, th0, 0.5, 140) : { x: th0 };
        // a single-paint "recipe" has no relative strength to learn
        var strengths = sub.map(function (_, j) { return j === 0 ? 1 : clampT(fitRes.x[j - 1]); });
        var model = paints.map(function (pt, i) {
            var j = rec.idx.indexOf(i);
            return j <= 0 ? pt : adjust(pt, strengths[j]);
        });
        var allIdx = paints.map(function (_, i) { return i; });
        var baseVols = new Array(paints.length).fill(0);
        rec.idx.forEach(function (i, j) { baseVols[i] = base[j]; });
        var labModel = labOfXyz(xyzOfR(mixR(model, baseVols, new Float64Array(N))));
        var offset = [observed[0] - labModel[0], observed[1] - labModel[1], observed[2] - labModel[2]];
        var predict = function (vols) {
            var lab = labOfXyz(xyzOfR(mixR(model, vols, new Float64Array(N))));
            return [lab[0] + offset[0], lab[1] + offset[1], lab[2] + offset[2]];
        };
        var before = deltaE2000(target, observed);
        var options = [];
        // 2a) one paint added
        var singles = [];
        allIdx.forEach(function (j) {
            var vols = baseVols.slice();
            var g = function (la) { var a = Math.exp(la); vols[j] = baseVols[j] + a; var d = deltaE2000(target, predict(vols)); vols[j] = baseVols[j]; return d + ADD_COST * a; };
            var bl = -7, bf = Infinity;
            for (var la = -7; la <= Math.log(maxAdd) + 1e-9; la += 0.35) { var f = g(la); if (f < bf) { bf = f; bl = la; } }
            var r = nelderMead(function (x) { return g(Math.min(Math.log(maxAdd), x[0])); }, [bl], 0.3, 30);
            var a = Math.exp(Math.min(Math.log(maxAdd), r.x[0]));
            var v2 = baseVols.slice(); v2[j] += a;
            singles.push({ adds: [{ i: j, a: a }], dE: deltaE2000(target, predict(v2)) });
        });
        singles.sort(function (x, y) { return x.dE - y.dE; });
        options = options.concat(singles.slice(0, 4));
        // 2b) two paints added (from the six most useful singles)
        var top = singles.slice(0, 6).map(function (s) { return s.adds[0].i; });
        for (var x = 0; x < top.length; x++) for (var y = x + 1; y < top.length; y++) {
            var j1 = top[x], j2 = top[y];
            var g2 = function (th) {
                var a1 = Math.exp(Math.min(Math.log(maxAdd), th[0])), a2 = Math.exp(Math.min(Math.log(maxAdd), th[1]));
                var vols = baseVols.slice(); vols[j1] += a1; vols[j2] += a2;
                return deltaE2000(target, predict(vols)) + ADD_COST * (a1 + a2);
            };
            var s1 = singles.filter(function (s) { return s.adds[0].i === j1; })[0];
            var r2 = nelderMead(g2, [Math.log(Math.max(1e-3, s1.adds[0].a)), -4], 0.8, 80);
            var a1 = Math.exp(Math.min(Math.log(maxAdd), r2.x[0])), a2 = Math.exp(Math.min(Math.log(maxAdd), r2.x[1]));
            var vv = baseVols.slice(); vv[j1] += a1; vv[j2] += a2;
            options.push({ adds: [{ i: j1, a: a1 }, { i: j2, a: a2 }], dE: deltaE2000(target, predict(vv)) });
        }
        options.forEach(function (o) {
            o.adds = o.adds.filter(function (ad) { return ad.a >= 0.004; })
                .map(function (ad) { return { i: ad.i, a: ad.a * total }; });
        });
        options = options.filter(function (o) { return o.adds.length && o.dE < before - 0.3; });
        options.sort(function (x, y) { return x.dE - y.dE; });
        return {
            before: before,
            options: options,
            strengths: rec.idx.map(function (i, j) { return { i: i, s: strengths[j] }; }),
            offset: offset
        };
    }

    /* ---- "your paints cannot reach this" → which tube would help ----------------
     * For each candidate pigment not in the palette, the best recipe that USES
     * it (with up to maxPaints−1 of the person's paints). Cheap on purpose:
     * pairs exhaustively, triples only from the palette's leading pairs. */
    function suggest(target, paints, candidates, opts) {
        opts = opts || {};
        var kmax = Math.max(2, Math.min(3, opts.maxPaints || 3));
        var base = solve(target, paints, { maxPaints: Math.min(2, kmax) });
        var leadPairs = base.ranked.filter(function (r) { return r.idx.length === 2; }).slice(0, 10);
        var out = [];
        candidates.forEach(function (c) {
            var all = paints.concat([c]), ci = all.length - 1;
            var ev = makeEval(all, target);
            var best = { dE: ev([ci], [1]), idx: [ci], vols: [1] };
            var tmp = [0, 0];
            for (var i = 0; i < paints.length; i++) {
                var idx = [i, ci];
                var g = function (t) { var s = 1 / (1 + Math.exp(-t)); tmp[0] = 1 - s; tmp[1] = s; return ev(idx, tmp); };
                var bt = 0, bf = Infinity;
                for (var t0 = -7.5; t0 <= 7.51; t0 += 0.5) { var f0 = g(t0); if (f0 < bf) { bf = f0; bt = t0; } }
                var r = nelderMead(function (x) { return g(x[0]); }, [bt], 0.25, 25);
                if (r.f < best.dE) { var s = 1 / (1 + Math.exp(-r.x[0])); best = { dE: r.f, idx: [i, ci], vols: [1 - s, s] }; }
            }
            if (kmax >= 3) {
                var v3 = [0, 0, 0];
                leadPairs.forEach(function (pr) {
                    var tri = [pr.idx[0], pr.idx[1], ci];
                    var h = function (th) { volsOf(th, v3); return ev(tri, v3); };
                    var r3 = nelderMead(h, thetaOf([pr.vols[0] * 0.95, pr.vols[1] * 0.95, 0.05]), 0.7, 60);
                    if (r3.f < best.dE) best = { dE: r3.f, idx: tri, vols: volsOf(r3.x, [0, 0, 0]) };
                });
            }
            out.push({ cand: c, rec: best });
        });
        out.sort(function (x, y) { return x.rec.dE - y.rec.dE; });
        return out;
    }

    /* Plain-language verdict bands for ΔE2000 (industry rules of thumb, same
       bands as color-pick-core.js so both tools speak the same language). */
    function verdictKey(dE) {
        if (dE < 1) return 'imperceptible';
        if (dE < 2) return 'barely';
        if (dE < 3.5) return 'side_by_side';
        if (dE < 5) return 'close';
        if (dE < 10) return 'different';
        return 'far';
    }

    return {
        N: N,
        LIB: LIB,
        libraryIds: libraryIds,
        pigment: pigment,
        prepare: prepare,
        adjust: adjust,
        composite: composite,
        ksOf: ksOf,
        rOfKs: rOfKs,
        xyzOfR: xyzOfR,
        labOfXyz: labOfXyz,
        labOfRgb: labOfRgb,
        rgbOfXyz: rgbOfXyz,
        rgbOfLab: rgbOfLab,
        hexOfRgb: hexOfRgb,
        deltaE2000: deltaE2000,
        mixR: mixR,
        mixXyz: mixXyz,
        mixLab: mixLab,
        mixRgb: mixRgb,
        paintRgb: paintRgb,
        tintRgb: tintRgb,
        solve: solve,
        recommend: recommend,
        usefulCounts: usefulCounts,
        toParts: toParts,
        correct: correct,
        suggest: suggest,
        verdictKey: verdictKey,
        MIN_FRAC: MIN_FRAC,
        _basis: { X: BX, Y: BY, Z: BZ }
    };
}));
